// SoftStatusChip: the app's one status chip as Antonin's export draws it (`src/components/StatusChip/StatusChip.tsx`):
// the semantic tint behind the semantic text, 4 px radius, 20 tall, 13/500 label; a "Draft" / "AI draft" label (or
// `draft`) is hollow with a dashed outline. It composes the Signal DS `StatusChip` in its soft tone, as the export does;
// the look (soft pairs, geometry, draft) is the bridge's "status chip" section, which already reaches every DS chip
// (`span[data-semantic][data-size]`, `data-chip-look="draft"`).
// `glyph` puts the status circle (StatusGlyph, 10 px, aria-hidden) in front of the word, 6 px apart (Antonin's export
// 22 Oct, the Day rows: `StatusChip` around `StatusGlyph label={false}` and the name, `gap: 6`).
// Stand-in (PROD-3310): the DS StatusChip paints its colours inline and has no draft look. Named apart from the DS
// export so it is not a shadow of it.
import type { ReactNode } from 'react';
import { StatusChip } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { StatusGlyph, type GlyphKind } from '@/shared/ui/components/status-glyph';

export type SoftStatusSemantic = 'success' | 'warning' | 'error' | 'info' | 'primary' | 'neutral';

export interface SoftStatusChipProps {
  semantic?: SoftStatusSemantic;
  /** Force the hollow dashed Draft look on or off. A "Draft" / "AI draft" label is a draft on its own. */
  draft?: boolean;
  /** The status circle drawn before the word (decorative: the word names the status). */
  glyph?: GlyphKind;
  children: ReactNode;
  className?: string;
}

const isDraftLabel = (children: ReactNode) => typeof children === 'string' && /^(ai\s+)?draft$/i.test(children.trim());

export function SoftStatusChip({ semantic = 'neutral', draft, glyph, children, className }: SoftStatusChipProps) {
  const isDraft = draft ?? isDraftLabel(children);
  return (
    <StatusChip
      semantic={semantic}
      tone="soft"
      data-chip-look={isDraft ? 'draft' : 'soft'}
      data-slot="pa-status-chip"
      data-chip-glyph={glyph ? '' : undefined}
      className={cn('pa-status-chip', className)}
    >
      {glyph ? <StatusGlyph kind={glyph} /> : null}
      {children}
    </StatusChip>
  );
}
